import type { OrderItemFulfilmentDetails } from '@skupreme/api-contracts/core';

const PENDING_SHIPMENT = 'Pending Shipment';

type OrderItem = {
  _id?: unknown;
  item_id?: unknown;
  qty?: number;
  state?: string;
  fulfilment_details?: OrderItemFulfilmentDetails[];
};

type ProviderItem = {
  _id?: unknown;
  item_id?: unknown;
  available?: number;
};

/** A provider reduced to the question this file asks: what does it hold? */
type ProviderStock = {
  items?: ProviderItem[];
};

/**
 * Whether the provider says what it holds. The Postgres orders API sends
 * providers without `items`; that is unknown stock, not an empty shelf.
 */
export const isProviderStockKnown = (provider?: ProviderStock) =>
  Array.isArray(provider?.items);

const toComparableId = (value: unknown) => {
  if (value === undefined || value === null) return '';
  return String(value);
};

export const getProviderItemForOrderItem = (
  providerItems: ProviderItem[] = [],
  orderItem: OrderItem = {},
) => {
  const orderItemIds = [orderItem.item_id, orderItem._id]
    .map(toComparableId)
    .filter(Boolean);

  if (!orderItemIds.length) return undefined;

  return providerItems.find(providerItem => {
    const providerItemIds = [providerItem?.item_id, providerItem?._id]
      .map(toComparableId)
      .filter(Boolean);

    return orderItemIds.some(orderItemId =>
      providerItemIds.includes(orderItemId),
    );
  });
};

export const getUnfulfilledOrderItemQty = (orderItem: OrderItem = {}) => {
  let amountRequired = orderItem.qty || 0;

  orderItem.fulfilment_details?.forEach(fulfillDetails => {
    amountRequired -= fulfillDetails?.qty || 0;
  });

  return amountRequired > 0 ? amountRequired : 0;
};

export const getProviderAvailableForOrderItem = (
  providerItems: ProviderItem[] = [],
  orderItem: OrderItem = {},
) => {
  const providerItem = getProviderItemForOrderItem(providerItems, orderItem);

  return providerItem?.available || 0;
};

export const getUnfulfillablePendingShipmentItemCount = (
  orderItems: OrderItem[] = [],
  currentProvider?: ProviderStock,
) => {
  if (currentProvider && !isProviderStockKnown(currentProvider)) {
    return 0;
  }

  let itemsNotFulfillable = 0;
  const providerItems = currentProvider?.items || [];

  for (const item of orderItems || []) {
    if (item.state !== PENDING_SHIPMENT) {
      continue;
    }

    const amountRequired = getUnfulfilledOrderItemQty(item);
    const available = getProviderAvailableForOrderItem(providerItems, item);

    if (available < amountRequired) {
      itemsNotFulfillable += 1;
    }
  }

  return itemsNotFulfillable;
};
